/**
 * Handicaps, course handicaps, and pop allocation.
 *
 * A "pop" is an extra stroke on a hole. Getting this module wrong quietly
 * corrupts every match in the tournament, so the rules are spelled out rather
 * than left as bare arithmetic.
 */

import type { Course, Hole, NineSelector, Player, QuotaConfig, TeeSet, TeeSetId } from './types'

/**
 * USGA rounds .5 away from zero. `Math.round(-0.5)` is `-0` in JavaScript, so
 * it cannot be used directly for plus handicaps.
 */
export function roundHalfAwayFromZero(x: number): number {
  return (x < 0 ? -1 : 1) * Math.round(Math.abs(x))
}

export function holesFor(course: Course, nine: NineSelector): Hole[] {
  const sorted = [...course.holes].sort((a, b) => a.number - b.number)
  if (nine === 'front') return sorted.filter((h) => h.number <= 9)
  if (nine === 'back') return sorted.filter((h) => h.number > 9)
  if (nine === 'full18') return sorted
  const wanted = new Set(nine.holes)
  return nine.holes
    .map((n) => sorted.find((h) => h.number === n))
    .filter((h): h is Hole => !!h && wanted.has(h.number))
}

export function findTee(course: Course, player: Player): TeeSet {
  const id = player.teeByCourse[course.id]
  const chosen = id ? course.tees.find((t) => t.id === id) : undefined
  if (chosen) return chosen

  /*
   * A tee he picked somewhere else, before the championship tips.
   *
   * `tees[0]` is Black on all five Bandon cards, and Setup only ever offered a
   * tee for the FIRST course — so eleven men had `{ 'bandon-dunes': 'green' }`
   * and nothing else, and seven of the nine rounds were quietly scored off the
   * tips. Eighty-six player-rounds carried the wrong course handicap, by one to
   * four strokes, and because slope scales the index it moved the differences
   * between players too, so match play was wrong and not merely inflated.
   *
   * Nothing complains about this: a course handicap has no obviously wrong
   * value. So the fallback stops being "the first tee in the list" and becomes
   * "the tee this man plays", which is knowable — every Bandon card uses the
   * same five tee ids, so a choice on one course names a real tee on all of
   * them. Falling through to `tees[0]` remains, for a player who has never
   * chosen anywhere.
   */
  for (const other of Object.values(player.teeByCourse)) {
    const match = course.tees.find((t) => t.id === other)
    if (match) return match
  }
  /*
   * The tips, because the list is longest-first — and this line is the reason
   * `unassignedTees` exists. It renders SOMETHING for a roster mid-setup, but
   * it is a guess wearing four digits of confidence, so `startProblems` refuses
   * to start a tournament while any active player would reach it.
   */
  return course.tees[0]!
}

/**
 * Course Handicap, WHS 2020 onwards.
 *
 * The `(rating - par)` term IS the cross-tee adjustment: a player off a harder
 * tee carries that tee's extra difficulty in their handicap, so two players on
 * different tees are already directly comparable. Adding a rating differential
 * on top of this — as is sometimes suggested — awards the same strokes twice.
 */
export function courseHandicap18(index: number, tee: TeeSet): number {
  return roundHalfAwayFromZero(index * (tee.slope / 113) + (tee.rating - tee.par))
}

/**
 * Nine-hole Course Handicap, using complementary split-nine rounding.
 *
 * If the club publishes nine-hole ratings, those win. Otherwise halve the
 * eighteen-hole figure and round the nine containing stroke index 1 UP, the
 * other DOWN.
 *
 * Why it matters here specifically: Monday plays Gozzer front, back, then front
 * again, and Tuesday plays eighteen then the front nine again. Rounding half-up
 * on both nines would give a 9-handicap 5 + 5 = 10 pops across an eighteen he
 * has played as two nines. Since CH18/2 is always either an integer or exactly
 * x.5, ceil and floor are exactly round-half-up and round-half-down with no
 * float slop, and `ceil(h) + floor(h) === CH18` holds for every integer CH18,
 * positive or negative.
 *
 * The extra stroke goes to the nine holding stroke index 1 because that is the
 * harder nine by the course's own reckoning — course data, not front/back
 * position, so it survives a shotgun start or a reversed routing.
 */
export function courseHandicap9(
  index: number,
  tee: TeeSet,
  holes: Hole[],
  ch18: number,
): number {
  const isFront = (holes[0]?.number ?? 1) <= 9
  const published = isFront ? tee.front : tee.back
  if (published) {
    /*
     * HALF the index against a nine-hole rating, not the whole of it.
     *
     * WHS nine-hole Course Handicap is (HI / 2) x Slope9/113 + (CR9 - Par9).
     * The index is an eighteen-hole figure and the rating it is being measured
     * against covers nine, so passing the full index returned exactly double —
     * an 18 index off a 36.0/113 nine got 18 strokes for nine holes instead of
     * 9. The fallback below halves correctly, so the two paths disagreed by a
     * factor of two depending only on whether the club had published a
     * nine-hole rating.
     *
     * Dormant until a course carries `front`/`back` ratings, which none does
     * today — and it would have gone off the first time one was added, on a
     * trip where seven of nine rounds are nines.
     */
    return roundHalfAwayFromZero(
      (index / 2) * (published.slope / 113) + (published.rating - published.par),
    )
  }
  const half = ch18 / 2
  return holes.some((h) => h.strokeIndex === 1) ? Math.ceil(half) : Math.floor(half)
}

/** Course handicap for whatever holes a round actually plays. */
export function courseHandicapFor(
  index: number,
  tee: TeeSet,
  holes: Hole[],
): number {
  const ch18 = courseHandicap18(index, tee)
  return holes.length > 9 ? ch18 : courseHandicap9(index, tee, holes, ch18)
}

/**
 * Spread a signed handicap across the holes actually being played, returning an
 * array parallel to `holes`.
 *
 * Note there is no "renumber the stroke indexes 1..9" step — sorting the played
 * holes by their card stroke index IS the local ranking, and renumbering is a
 * redundant intermediate that only creates room for off-by-one errors.
 *
 * A negative handicap is a plus player GIVING strokes back, which come off the
 * EASIEST holes. That is why the ordering flips rather than the sign alone.
 */
export function allocatePops(holes: Hole[], signedHandicap: number): number[] {
  const n = holes.length
  if (n === 0) return []
  const sign = signedHandicap < 0 ? -1 : 1
  const total = Math.abs(signedHandicap)
  const base = Math.floor(total / n)
  const extra = total % n

  const order = holes
    .map((h, i) => ({ i, si: h.strokeIndex }))
    .sort((a, b) => (sign > 0 ? a.si - b.si : b.si - a.si))

  const out = new Array<number>(n).fill(base)
  for (let k = 0; k < extra; k++) out[order[k]!.i]! += 1
  return out.map((v) => sign * v)
}

/**
 * Pops for every member of a match.
 *
 * `relativeToLowInUnit` is how the Woodsmen play: the low handicap in the
 * contest plays off scratch and everyone else gets the difference, so a 10
 * against a 5 pops on the 5 hardest holes of the holes being played.
 */
export function allocationsFor(
  members: { id: string; courseHandicap: number }[],
  holes: Hole[],
  basis: 'relativeToLowInUnit' | 'relativeToLowInGroup' | 'full',
  allowance = 1,
): Record<string, number[]> {
  const scaled = members.map((m) => ({
    id: m.id,
    ch: Math.round(m.courseHandicap * allowance),
  }))
  const low = scaled.length ? Math.min(...scaled.map((m) => m.ch)) : 0
  const out: Record<string, number[]> = {}
  for (const m of scaled) {
    out[m.id] = allocatePops(holes, basis === 'full' ? m.ch : m.ch - low)
  }
  return out
}

export const netScore = (gross: number, pops: number): number => gross - pops

/**
 * Quota starting target.
 *
 * The sheet says each golfer's target is "as indicated on your scorecard", so
 * an explicit override always wins over the derived value.
 */
export function quotaTarget(
  cfg: QuotaConfig,
  holeCount: number,
  courseHandicapForHoles: number,
  override: number | null,
): number {
  if (override != null) return override
  const base = cfg.quotaFormula.pointsPerPar * holeCount
  return base - (cfg.quotaFormula.subtractHandicap ? courseHandicapForHoles : 0)
}

/**
 * Recommend a tee set from handicap index. Deliberately coarse — a starting
 * point for the admin to override, not a ruling.
 */
export function recommendTee(
  index: number,
  tees: TeeSet[],
  /*
   * Tees whose own par is not believable — see `teesWithBadPar`.
   *
   * Passed in rather than computed here because this takes a tee list, not a
   * course, and the caller has the course. Optional so existing callers keep
   * working; when it is supplied, a suspect tee is never the ★.
   *
   * This is the guard that matters most for the long handicaps. The rule below
   * sends anybody over 18 to the SHORTEST tee, and the shortest tees are exactly
   * the ones carrying the wrong par — so the recommendation was worst precisely
   * where it was trusted most.
   */
  unusable: Set<TeeSetId> = new Set(),
): TeeSet | null {
  const usable = tees.filter((t) => !unusable.has(t.id))
  // If every tee is suspect, recommending nothing is more honest than
  // recommending the least bad one.
  if (usable.length === 0) return null
  const byLength = [...usable].sort((a, b) => b.totalYards - a.totalYards)
  const pick = index <= 4 ? 0 : index <= 10 ? 1 : index <= 18 ? 2 : byLength.length - 1
  return byLength[Math.min(pick, byLength.length - 1)]!
}

/**
 * The number this man actually plays off.
 *
 * Two indexes are stored — his current one and his lowest of the last twelve
 * months — and exactly one of them decides his strokes. This is the only place
 * that choice is made, so that a course handicap, a tee recommendation, a
 * pairing and the text pasted into the group thread cannot disagree about it.
 *
 * Falls back to the current index whenever the low one is missing, which is the
 * normal state for anybody nobody has looked up in GHIN. Choosing "low" for a
 * man with no low on file must not hand him a zero and a card full of pops.
 */
export function playingIndex(player: {
  handicapIndex: number
  lowIndex?: number | null
  playsOff?: 'current' | 'low'
}): number {
  return player.playsOff === 'low' && typeof player.lowIndex === 'number'
    ? player.lowIndex
    : player.handicapIndex
}

/**
 * What is wrong with a tee's own numbers, if anything.
 *
 * `courseHandicap18` is `index x slope/113 + (rating - par)`, and that `par` is
 * THE TEE'S par, not the course's. Get it wrong and the error is not subtle: it
 * lands one-for-one in strokes, on every hole, for everybody on that tee.
 *
 * Which is what the seeded cards do. Every tee was given the course par, so
 * Sheep Ranch Royal Blue — 3,943 yards over eighteen holes, rated 61.0 — is
 * recorded as a par 72 and hands a 10 index a course handicap of MINUS TWO. He
 * would be giving strokes back to the field off the shortest tee on the property.
 *
 * The tell is `rating - par`. A real set of tees sits in a narrow band: the
 * championship tees rate a stroke or four above par, and the forward tees a few
 * below. Well outside that band means the par belongs to a different tee, because
 * a rating is measured from the ground and a par is typed in by hand.
 *
 * Deliberately a report rather than a throw. Two of these courses are in live
 * tournaments and the app has to keep working — the job here is to make the fault
 * visible where somebody can act on it, and to keep `recommendTee` from handing
 * out a tee whose numbers nobody trusts.
 */
export interface TeeProblem {
  teeId: TeeSetId
  teeName: string
  /** `par` = its par is probably the course's, not its own. */
  kind: 'par' | 'yardage' | 'slope'
  detail: string
}

/**
 * How far `rating - par` may stray before the par is not believable.
 *
 * Chosen from the seeded data rather than from a rule of thumb: every Black,
 * Green and Gold tee across the five courses sits between +3.9 and -4.1, and
 * those are the tees whose par genuinely IS the course par. Every Orange and
 * Royal Blue sits between -4.4 and -11.0. The band admits the first group and
 * rejects the second, which is exactly the split that needs making.
 */
const RATING_MINUS_PAR = { min: -4.2, max: 6 }

export function teeProblems(course: {
  par: number
  tees: TeeSet[]
  holes: { yards: Record<TeeSetId, number> }[]
}): TeeProblem[] {
  const out: TeeProblem[] = []
  const byLength = [...course.tees].sort((a, b) => b.totalYards - a.totalYards)

  for (const tee of course.tees) {
    const gap = tee.rating - tee.par
    if (gap < RATING_MINUS_PAR.min || gap > RATING_MINUS_PAR.max) {
      out.push({
        teeId: tee.id,
        teeName: tee.name,
        kind: 'par',
        detail:
          `rated ${tee.rating} off a par of ${tee.par} (${gap.toFixed(1)}). ` +
          `Almost certainly the course par rather than this tee's, which moves ` +
          `every course handicap on it by about ${Math.round(Math.abs(gap))} strokes.`,
      })
    }

    // A yardage that does not add up means a hole was mistyped, and the hole is
    // what a pop is allocated against.
    const summed = course.holes.reduce((s, h) => s + (h.yards[tee.id] ?? 0), 0)
    if (summed > 0 && summed !== tee.totalYards) {
      out.push({
        teeId: tee.id,
        teeName: tee.name,
        kind: 'yardage',
        detail: `holes add up to ${summed} yards but the tee says ${tee.totalYards}.`,
      })
    }
  }

  /*
   * Slope should not rise as a tee gets shorter. It is not a law of nature — a
   * short tee over the same hazards can genuinely play harder for a bogey golfer
   * — so this reports rather than rejects. It caught a real transposition once.
   */
  for (let i = 1; i < byLength.length; i += 1) {
    const longer = byLength[i - 1]!
    const shorter = byLength[i]!
    if (shorter.slope > longer.slope) {
      out.push({
        teeId: shorter.id,
        teeName: shorter.name,
        kind: 'slope',
        detail:
          `slope ${shorter.slope} is higher than ${longer.name}'s ${longer.slope} ` +
          `despite being ${longer.totalYards - shorter.totalYards} yards shorter. ` +
          `Possible, but check the two were not swapped.`,
      })
    }
  }

  return out
}

/** Tees whose own par is not believable, so nothing should recommend them. */
export function teesWithBadPar(course: {
  par: number
  tees: TeeSet[]
  holes: { yards: Record<TeeSetId, number> }[]
}): Set<TeeSetId> {
  return new Set(teeProblems(course).filter((p) => p.kind === 'par').map((p) => p.teeId))
}

/**
 * What is wrong with the card's own eighteen rows — the per-hole numbers, where
 * `teeProblems` covers the per-tee ones.
 *
 * This exists because the card is now editable, and an editable card can be
 * half-typed. The checks run live rather than on save, so the report has to be
 * honest about severity: everything here except `convention` is a card the app
 * cannot score correctly. `convention` is advice — the Bandon cards keep odd
 * stroke indexes on the front nine and even on the back, so a violation is
 * legal but usually means two holes were transposed while typing, which is
 * exactly the mistake nothing else can see.
 */
export interface CardProblem {
  /** `convention` is a warning; every other kind is a card that scores wrong. */
  kind: 'strokeIndex' | 'par' | 'yardage' | 'convention'
  detail: string
}

/**
 * The bands a believable hole yardage sits in. Sixty is under the shortest par
 * three anybody prints; seven hundred is past the longest par five on any of
 * these cards. Outside them is a typo — 3986 pasted into a hole, a dropped
 * digit — and a wrong yardage is only cosmetic until somebody checks a tee's
 * total against it.
 */
const HOLE_YARDS = { min: 60, max: 700 }

export function cardProblems(course: {
  par: number
  tees: { id: TeeSetId; name: string }[]
  holes: { number: number; par: number; strokeIndex: number; yards: Record<TeeSetId, number> }[]
}): CardProblem[] {
  const out: CardProblem[] = []
  const holes = [...course.holes].sort((a, b) => a.number - b.number)
  const n = holes.length

  /*
   * Stroke index must use each of 1..N exactly once. This is the single most
   * consequential field in the app — it decides every pop — and unlike a wrong
   * par, a duplicated or missing index never looks wrong on screen: the card
   * renders, the round scores, and two players quietly pop on the same hole
   * while a hole that should pop never does.
   */
  const at = new Map<number, number[]>()
  for (const h of holes) at.set(h.strokeIndex, [...(at.get(h.strokeIndex) ?? []), h.number])

  let siBroken = false
  for (const [si, where] of [...at].sort((a, b) => a[0] - b[0])) {
    if (!Number.isInteger(si) || si < 1 || si > n) {
      siBroken = true
      out.push({
        kind: 'strokeIndex',
        detail: `hole ${where.join(' and ')} carries stroke index ${si} — a ${n}-hole card runs 1 to ${n}.`,
      })
    } else if (where.length > 1) {
      siBroken = true
      out.push({
        kind: 'strokeIndex',
        detail: `stroke index ${si} appears ${where.length} times, on holes ${where.join(' and ')}.`,
      })
    }
  }
  const missing = Array.from({ length: n }, (_, i) => i + 1).filter((si) => !at.has(si))
  if (missing.length > 0) {
    siBroken = true
    out.push({
      kind: 'strokeIndex',
      detail: `no hole carries stroke index ${missing.join(', ')} — every number from 1 to ${n} must appear exactly once.`,
    })
  }

  // Par is bounded by what golf prints — no card anywhere here carries a 2 or
  // a 7, so one is a typo — and the sum is checked against the course's own
  // figure, because quota targets and the strokeNet start are counted from it.
  for (const h of holes) {
    if (!Number.isInteger(h.par) || h.par < 3 || h.par > 6) {
      out.push({
        kind: 'par',
        detail: `hole ${h.number} has a par of ${h.par} — pars run 3 to 6.`,
      })
    }
  }
  const parSum = holes.reduce((s, h) => s + h.par, 0)
  if (parSum !== course.par) {
    out.push({
      kind: 'par',
      detail: `the holes add up to par ${parSum} but the course says ${course.par}.`,
    })
  }

  /*
   * A missing yardage (or a 0) is allowed — it renders as a dot, and the
   * transcription rule has always been that an absent number beats an invented
   * one. A present number outside the believable band is flagged, because that
   * is a slipped digit and it will surface later as a tee total that will not
   * reconcile. Only the tees still on the card are read: a removed tee's old
   * column is kept in case it comes back, and nagging about numbers nobody can
   * see would send somebody hunting for a cell that is not on screen.
   */
  for (const tee of course.tees) {
    for (const h of holes) {
      const y = h.yards[tee.id]
      if (y === undefined || y === 0) continue
      if (y < HOLE_YARDS.min || y > HOLE_YARDS.max) {
        out.push({
          kind: 'yardage',
          detail: `${tee.name} reads ${y} yards on hole ${h.number} — anything under ${HOLE_YARDS.min} or over ${HOLE_YARDS.max} is a mistyped cell.`,
        })
      }
    }
  }

  /*
   * The odd/even convention, checked only once the permutation itself is
   * whole. Every Bandon card puts the odd indexes on the front nine and the
   * even on the back, so a valid 1..18 with an even number on the front almost
   * always means two indexes were swapped between nines — the transposition
   * that reads perfectly and pops the wrong holes all week. While the
   * permutation is still broken this would only restate that error in a second
   * voice, so it waits.
   */
  if (n === 18 && !siBroken) {
    for (const h of holes) {
      const front = h.number <= 9
      const odd = h.strokeIndex % 2 === 1
      if (front !== odd) {
        out.push({
          kind: 'convention',
          detail:
            `hole ${h.number} carries stroke index ${h.strokeIndex} — these cards keep odd ` +
            `indexes on the front nine and even on the back, so check this against the ` +
            `physical card; it usually means two indexes were transposed.`,
        })
      }
    }
  }

  return out
}

/**
 * Players whose tee would be GUESSED rather than chosen.
 *
 * `findTee` has a chain of fallbacks and the last one is `course.tees[0]` — the
 * tips, because the list is ordered longest first. A man with an EMPTY
 * `teeByCourse` rides that fallback on every course, silently: the dry run
 * carried Babcock that way for a week, pricing him off the Black at 143 slope
 * while his foursome played the Green, and the extra course handicap it minted
 * handed him a pop his opponent never agreed to. Nothing looked wrong — the CH
 * was arithmetically perfect for a tee nobody had picked.
 *
 * The fallback stays (a screen must render something), but it must never make
 * it into a STARTED tournament: this feeds `startProblems`, which blocks the
 * start button until every active player has at least one real assignment for
 * the fallback chain to work from.
 */
export function unassignedTees(t: {
  players: { id: string; displayName: string; active: boolean; teeByCourse: Record<string, string> }[]
}): string[] {
  return t.players
    .filter((p) => p.active && Object.keys(p.teeByCourse ?? {}).length === 0)
    .map((p) => p.displayName)
}
